import React from 'react';
import { engagementSteps } from '../../data/mock';
import { ArrowRight } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/use-scroll-animation';

const StepCard = ({ step, index, totalSteps }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <div 
      ref={ref}
      className={`relative group transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Premium Connector line */}
      {index < totalSteps - 1 && (
        <div className="hidden md:block absolute top-12 left-full w-full h-[2px] bg-gradient-to-r from-slate-700 via-slate-600 to-transparent z-0">
          <ArrowRight className="absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400/60 group-hover:text-amber-400 transition-colors" />
        </div>
      )}
      
      <div className="relative z-10">
        {/* Premium Step number */}
        <div className="text-7xl lg:text-8xl font-extrabold bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 bg-clip-text text-transparent mb-6 group-hover:from-slate-700 group-hover:via-slate-600 group-hover:to-slate-700 transition-all duration-500 group-hover:scale-105">
          {step.step}
        </div>
        
        {/* Premium Title */}
        <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-amber-50 transition-colors">
          {step.title}
        </h3>
        
        {/* Premium Description */}
        <p className="text-slate-300 leading-relaxed text-base font-medium">
          {step.description}
        </p>
      </div>
    </div>
  );
};

export const EngagementSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="py-32 lg:py-40 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Premium background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(251,191,36,0.1),transparent_50%)]" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl animate-float-slow" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Premium Section Header */}
        <div 
          ref={headerRef}
          className={`max-w-3xl mb-20 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-sm font-bold text-amber-400/80 uppercase tracking-widest mb-4 inline-block">How We Engage</span>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
            Simple. Direct. Effective.
          </h2>
          <p className="mt-6 text-xl text-slate-300 leading-relaxed font-medium">
            Get from idea to impact quickly.
          </p>
        </div>

        {/* Premium Steps */}
        <div className="grid md:grid-cols-3 gap-10 lg:gap-16">
          {engagementSteps.map((step, index) => (
            <StepCard
              key={step.id}
              step={step}
              index={index}
              totalSteps={engagementSteps.length}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
